import { AppContext } from "App";

import { ISectionLogger } from "@multiweb/logging";
import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { ICommunicationManager } from "OW.Remoting/Remoting/ICommunicationManager";
import { RequestContextWithoutTransactionContextId } from "OW.Remoting/Remoting/RequestContextWithoutTransactionContextId";
import { ASServices } from "../../../../OW.Remoting/Basic/ServiceEndpoints";
import { AppCacheProperties } from "../../../../OW.Remoting/Remoting/AppMemCache";
import { ApplicationCache } from "../../../../OW.Remoting/Remoting/Cache/IAppCache";

/**
 * výčet systémových operací
 */
export enum SystemOperation {
    FRMLOC = "FRMLOC",
    SHOWAUDITDATA = "SHOWAUDITDATA",
    SHOWWFSTATUS = "SHOWWFSTATUS",
    SHOWWFPARTICIPANTS = "SHOWWFPARTICIPANTS",
    SHOWWFHISTORY = "SHOWWFHISTORY",
    SHOWRECORDFIRST = "SHOWRECORDFIRST",
    SHOWFULLFORMINFO = "SHOWFULLFORMINFO",
    EDITIMPPROFILE = "EDITIMPPROFILE",
    EDITIMPSHORTCUTS = "EDITIMPSHORTCUTS",
    GETVERSIONINFO = "GETVERSIONINFO",
    ADVBRDCRMB = "ADVBRDCRMB",
    aud = "aud",
}

/**
 * Kolekce vyhodnocených práv
 */
interface IRightResultCollection {
    [attribute: string]: boolean;
}

/**
 * Rozhraní pro třídu vyhodnocující práva k systémovým operacím
 * @includeToDoc
 */
export interface ISystemOperationRights {
    /**
     * Vrací právo pro zadanou systémovou operaci
     * @param operation Název operace
     */
    get(operation: SystemOperation | string): Promise<boolean>;

    /**
     * Pročistí cache vyhodnocených práv
     */
    clearCache(): void;
}

export class ISystemOperationRights {
    private static _methods = ["get", "clearCache"];
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Třída pro kešování práv na systémové operace
 */
export class SystemOperationRights implements ISystemOperationRights {
    private readonly _logger: ISectionLogger;

    /** Práva která mají být načtena prvním dotazem na server */
    private _initEvalRights: (SystemOperation | string)[] = [SystemOperation.EDITIMPPROFILE, SystemOperation.SHOWAUDITDATA, SystemOperation.SHOWWFHISTORY, SystemOperation.SHOWWFPARTICIPANTS, SystemOperation.SHOWWFSTATUS];
    private _initCachePromise: Promise<IRightResultCollection> | false = false;
    private readonly _communicationManager: ICommunicationManager;

    constructor(communicationManager: ICommunicationManager, logger: ISectionLogger) {
        this._communicationManager = communicationManager;
        this._logger = logger;
    }

    /** Získá práva pro zadanou systémovou operaci
     * @param operation Systémová operace
     * @param defaultValue Výchozí hodnota, pokud uživatel nemá právo nastaveno
     */
    public async get(operation: SystemOperation | string, defaultValue: boolean = false): Promise<boolean> {
        let cachedValues = ApplicationCache && ApplicationCache.instance.getValue<IRightResultCollection>(AppCacheProperties.systemOperationRights);
        if (!cachedValues && !this._initCachePromise)
            this._initCachePromise = this._initCache(operation);
        await this._initCachePromise;

        cachedValues = ApplicationCache && ApplicationCache.instance.getValue<IRightResultCollection>(AppCacheProperties.systemOperationRights);
        if (!cachedValues) {
            this._logger.error(`Unable to evaluate right for '${operation}'. Failed to evaluate default rights.`);
        } else if (!Object.prototype.hasOwnProperty.call(cachedValues, operation)) { //keš existuje, ale nemá vyhodnocené zadané právo > doptá se serveru.
            const operationRight = await this._evaluateSystemOperationRights([operation], defaultValue);
            if (!operationRight) {
                this._logger.error(`Unable to evaluate right for '${operation}'. Server responded with invalid value`);
            } else {
                cachedValues[operation] = operationRight[operation];
                if (ApplicationCache)
                    ApplicationCache.instance.setValue(AppCacheProperties.systemOperationRights, cachedValues);
            }
        }
        return cachedValues && cachedValues[operation] ? cachedValues[operation] : defaultValue;
    }

    /**
     * Inicializuje cache
     */
    private async _initCache(operation: string | SystemOperation): Promise<IRightResultCollection> {
        const firstBatch = [...this._initEvalRights];
        if (!firstBatch.includes(operation))
            firstBatch.push(operation);
        const cachedValues = await this._evaluateSystemOperationRights(firstBatch, false);
        if (ApplicationCache)
            ApplicationCache.instance.setValue(AppCacheProperties.systemOperationRights, cachedValues);
        return cachedValues;
    }

    /**
     * Hromadně vyhodnocuje oprávnění na systémové operace ze serveru
     * @param operations Kolekce operací, které se mají vyhodnotit
     * @param defaultValue Výchozí hodnota, pokud pro danou operaci nejsou definována práva
     */
    private async _evaluateSystemOperationRights(operations: (SystemOperation | string)[], defaultValue = false): Promise<IRightResultCollection> {
        try {
            // Systémové operace jsou na speciální právo, které se nevztahuje k business datům. Proto není potřeba rolí ani transakčního kontextu.
            const requestContext = this._getRequestContext();
            return await this._communicationManager.callPOSTRequest("EvaluateSystemOperationRightsForUser", ASServices.owManager, { defaultValue: defaultValue }, operations, undefined, requestContext);
        } catch (e) {
            this._logger.error("Failed to fetch SystemOperation rights.", e);
            return {};
        }
    }

    public clearCache(): void {
        if (ApplicationCache) {
            ApplicationCache.instance.setValue(AppCacheProperties.systemOperationRights, undefined);
            this._initCachePromise = false;
        }
    }

    /**
     * Systémové operace editačnímu kontextu nepodléhají
     * @param cancellationToken Umožňuje zrušit běžící dotaz
     */
    private _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContextWithoutTransactionContextId {
        return {
            roles: AppContext.instance.activeUserRolesIds,
            cancellationToken,
        };
    }
}
